"use client"
import { startTransition, useEffect, useMemo, useRef, useState } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useReview, reviewActions, getReviewState } from "./review-store"
import { reviewHref, reviewPlaces, findAndHighlight } from "./jump-to-string"
import { filterQueue } from "./filter-queue"
import { locateState, type LocateState } from "./locate-state"
import { ReviewPanel, type ReviewPanelHandle } from "./review-panel"
import { MobileReviewView } from "./mobile-review-view"
import { useIsMobile } from "../editor/use-is-mobile"
import { UndoToast } from "./undo-toast"
import { undoLast, flushAll } from "./pending-commits"
import { setReviewPr } from "./review-pr"
import { isUndoChord } from "./undo-keys"
import { ReviewOverviewHeader } from "./review-overview"
import { PrStatusBoard } from "./pr-status-board"
import { BatchTable } from "./batch-table"
import { TIER_LABEL, TIER_MARKER, TIER_ARIA, TIER_ORDER } from "./tier-labels"
import { loadStringLocations, locationFor } from "@/lib/review/string-locations"
import type { StringLocationIndex } from "@/scripts/string-locations-build-core"
import type { Overview } from "@/lib/review/overview"
import type { QueueItem } from "@/lib/review/queue"
import type { Tier } from "@/scripts/review-ledger"
import type { Glossary } from "@/lib/review/glossary"

// The cockpit runs the value-QA gate client-side using the term base the queue
// route ships in its payload. An empty glossary just means no glossary-tier
// warnings; the placeholder/tag error checks are glossary-independent.
const EMPTY_GLOSSARY: Glossary = { terms: [] }

// Render the list in chunks — the full backlog can run to hundreds of strings,
// which is unusable to scroll and slow to paint all at once.
const PAGE_SIZE = 50

// A jump/auto-advance to another locale remounts this body (its `[locale]` route
// segment changes), which would otherwise reset the search box, tier chips and
// locale picker on every step. Persist them in session storage so the filtered
// view the reviewer set up survives stepping the worklist. (The `current` item
// itself survives via the review-store singleton.)
const SS_QUERY = "ahead-review-query"
const SS_TIERS = "ahead-review-tiers"
const SS_LOCALE = "ahead-review-locale"
const readSession = (key: string): string | null =>
  typeof window === "undefined" ? null : sessionStorage.getItem(key)

const LOC_MARKER: Record<LocateState, string> = {
  page: "",
  chrome: "⌂",
  template: "{ }",
}

function isEditableTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable
}

export function ReviewWorklistBody({
  idToken,
  scopeOpen = false,
  onScopeAutoOpen,
}: {
  idToken: string
  /** When true, show the scope / who-can-help / copy-link header (folded behind
   *  the cockpit's "Your scope" toggle by default). */
  scopeOpen?: boolean
  onScopeAutoOpen?: () => void
}) {
  const isMobile = useIsMobile()
  const { queue, current, view } = useReview()
  const [locale, setLocale] = useState<string | null>(null)
  const [locales, setLocales] = useState<string[]>([])
  const [, setTiers] = useState<string[]>([])
  const [overview, setOverview] = useState<Overview | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [stringIndex, setStringIndex] = useState<StringLocationIndex | null>(
    null
  )
  const [glossary, setGlossary] = useState<Glossary>(EMPTY_GLOSSARY)
  // Queue-fetch progress. Declared here (above the discovery effect) because that
  // effect can prime the queue and mark it fetched in one shot.
  const [fetching, setFetching] = useState(false)
  const [everFetched, setEverFetched] = useState(false)
  const panelRef = useRef<ReviewPanelHandle>(null)
  // The locale whose queue the discovery call already primed (see effect 1). The
  // per-locale effect consumes it once to skip the redundant first fetch.
  const primedLocaleRef = useRef<string | null>(null)
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const reviewParam = searchParams.get("review")
  // Deep link from the "Translation review" check (`?pr=<n>`): scope the worklist
  // to this PR's pending strings. `prActive` flips off if the PR's gate data
  // isn't readable (gate not run yet, or token missing the Checks scope) — the
  // worklist then falls back to the reviewer's whole-scope backlog, so the deep
  // link always lands in the cockpit regardless.
  const prParam = searchParams.get("pr")
  const pr = prParam && /^\d+$/.test(prParam) ? Number(prParam) : null
  const [prActive, setPrActive] = useState(pr != null)
  const [query, setQuery] = useState(() => readSession(SS_QUERY) ?? "")
  const [visible, setVisible] = useState(PAGE_SIZE)
  // Tier filter. `null` = the default selection (high-traffic pre-selected);
  // a Set once the reviewer touches a chip. Chips render only for tiers actually
  // present in the queue — which already reflects the reviewer's scope, so a
  // "legal" chip appears only when they have legal scope.
  const [tierSel, setTierSel] = useState<Set<Tier> | null>(() => {
    const raw = readSession(SS_TIERS)
    return raw ? new Set(JSON.parse(raw) as Tier[]) : null
  })
  const availableTiers = useMemo(() => {
    const present = new Set(queue.map((i) => i.tier))
    return TIER_ORDER.filter((t) => present.has(t))
  }, [queue])
  const defaultTierSel = useMemo(
    () =>
      new Set<Tier>(
        availableTiers.includes("high-traffic")
          ? ["high-traffic"]
          : availableTiers
      ),
    [availableTiers]
  )
  const effTierSel = tierSel ?? defaultTierSel
  function toggleTier(t: Tier) {
    const next = new Set(effTierSel)
    if (next.has(t)) next.delete(t)
    else next.add(t)
    setTierSel(next)
  }
  // Reset the window whenever the search or tier filter changes so matches show
  // from the top.
  useEffect(() => setVisible(PAGE_SIZE), [query, tierSel])
  // Persist the filtered view so it survives the remount a cross-locale jump
  // triggers (see SS_* above).
  useEffect(() => {
    sessionStorage.setItem(SS_QUERY, query)
  }, [query])
  useEffect(() => {
    if (tierSel)
      sessionStorage.setItem(SS_TIERS, JSON.stringify(Array.from(tierSel)))
  }, [tierSel])
  // Keep each item's original queue index — open()/goto() address the full queue,
  // not the filtered view. An empty tier selection means "show all" (so toggling
  // every chip off never strands the reviewer on a blank list).
  const filtered = useMemo(
    () =>
      queue
        .map((item, i) => ({ item, i }))
        .filter(
          ({ item }) => effTierSel.size === 0 || effTierSel.has(item.tier)
        )
        .filter(({ item }) => filterQueue([item], query).length > 0),
    [queue, query, effTierSel]
  )

  // Strings that match the current search but are excluded *only* by the active
  // tier filter. Surfacing this count stops the filter from silently swallowing
  // matches — e.g. searching a glossary key while the default high-traffic chip
  // is on otherwise reads as a flat "0 of N shown" with no clue why (AHD-7907).
  const hiddenByTier = useMemo(() => {
    if (effTierSel.size === 0) return 0
    return queue.filter(
      (item) =>
        !effTierSel.has(item.tier) && filterQueue([item], query).length > 0
    ).length
  }, [queue, query, effTierSel])

  // The crawl index: precise page per key + chromeLike. Until it resolves we
  // pass null and fall back to the namespace heuristic (current behaviour).
  const indexLoc = (item: QueueItem) =>
    stringIndex ? locationFor(stringIndex, item.key) : null
  const localeOf = (item: QueueItem): LocateState =>
    locateState(item, item.templateFlag, indexLoc(item))
  const currentLocate = current ? localeOf(current) : "page"
  // The pages an off-page (shared-UI / template) string renders on, so the panel
  // can offer the reviewer each one to confirm the translation in context.
  const currentPlaces = current ? reviewPlaces(current, indexLoc(current)) : []

  // The best single jump for the current item (the same target the auto-advance
  // uses). Split off the `?review=…` query to compare against the live pathname
  // and to prefetch the route (the query doesn't change the static page).
  const currentHref = current ? reviewHref(current, indexLoc(current)) : null
  const currentHrefPath = currentHref ? currentHref.split("?")[0] : null
  // Offer an explicit "View on page" link only for a *page* string the reviewer
  // isn't already viewing — chrome/template strings use the PlacesPanel instead,
  // and there's nothing to jump to when you're already on the page.
  const pageHref =
    currentLocate === "page" &&
    currentHref &&
    currentHrefPath &&
    currentHrefPath !== pathname
      ? currentHref
      : null

  // Prefetch the current item's target route (and any alternate pages) the moment
  // it becomes current, so the jump — whether auto-advance or the "View on page"
  // link — is an instant soft swap rather than a click-time RSC fetch that read
  // as a hard refresh on the first jump off the homepage. Prefetch the bare route
  // (no `?review=`): the page is static, so one prefetch warms every string on it.
  useEffect(() => {
    if (!currentHrefPath) return
    router.prefetch(currentHrefPath)
    for (const p of currentPlaces) router.prefetch(p.href.split("?")[0])
    // currentPlaces derives from current + stringIndex; keep deps minimal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHrefPath, stringIndex])

  // Load the crawl index once (edit-only chunk; lazy import).
  useEffect(() => {
    loadStringLocations().then(setStringIndex)
  }, [])

  // 0. PR-wide backlog + who-can-help (shown to everyone, scope or not).
  useEffect(() => {
    fetch(`/api/review/overview`, {
      headers: { "User-Authorization": `Bearer ${idToken}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("load"))))
      .then(setOverview)
      .catch(() => setLoadError(true))
  }, [idToken])

  // Tell the sign-off posters which PR (if any) we're scoped to, so a re-push-free
  // green flip targets the right check. null once we've fallen back to whole-scope.
  useEffect(() => {
    setReviewPr(prActive ? pr : null)
    return () => setReviewPr(null)
  }, [prActive, pr])

  // 1. discover the reviewer's scope (no-locale call returns {items:[], scope}).
  // In PR-deep-link mode this hits pr-queue, which narrows the locale picker to
  // the locales this PR actually touched; `prGate:false` → fall back to whole scope.
  useEffect(() => {
    const url =
      prActive && pr ? `/api/review/pr-queue?pr=${pr}` : `/api/review/queue`
    fetch(url, {
      headers: { "User-Authorization": `Bearer ${idToken}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("load"))))
      .then((d) => {
        if (prActive && d.prGate === false) {
          setPrActive(false) // re-runs this effect against the whole-scope queue
          return
        }
        const ls: string[] = d.scope?.locales ?? []
        setLocales(ls)
        setTiers(d.scope?.tiers ?? [])
        // Restore the persisted locale if it's still in scope, else first in scope.
        const persisted = readSession(SS_LOCALE)
        const chosen =
          (persisted && ls.includes(persisted) ? persisted : ls[0]) ?? null
        // The discovery call also serves the queue for its default locale. Adopt
        // it when we're landing on that same locale so the per-locale effect can
        // skip the redundant second fetch. If the reviewer ends up on a different
        // locale (e.g. a persisted pick), effect 2 drops this prime and fetches.
        if (chosen && d.locale === chosen && Array.isArray(d.items)) {
          reviewActions.setQueue(d.items)
          if (d.glossary) setGlossary(d.glossary as Glossary)
          primedLocaleRef.current = chosen
          setEverFetched(true)
        }
        setLocale((cur) => cur ?? chosen)
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoaded(true))
  }, [idToken, prActive, pr])

  // 2. load the queue for the chosen locale
  useEffect(() => {
    if (!locale) return
    // The discovery call already primed this locale's queue (effect 1) — consume
    // that once and skip the redundant fetch. Otherwise drop any prime (it was for
    // a locale we didn't land on) and fetch, so a stale prime can't be reused.
    if (primedLocaleRef.current === locale) {
      primedLocaleRef.current = null
      return
    }
    primedLocaleRef.current = null
    let cancelled = false
    setFetching(true)
    const url =
      prActive && pr
        ? `/api/review/pr-queue?pr=${pr}&locale=${encodeURIComponent(locale)}`
        : `/api/review/queue?locale=${encodeURIComponent(locale)}`
    fetch(url, {
      headers: { "User-Authorization": `Bearer ${idToken}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return
        if (prActive && d.prGate === false) {
          setPrActive(false) // fall back; the effect re-runs with the whole-scope queue
          return
        }
        reviewActions.setQueue(d.items ?? [])
        if (d.glossary) setGlossary(d.glossary as Glossary)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setFetching(false)
          setEverFetched(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [idToken, locale, prActive, pr])
  // Persist the chosen locale so it survives the cross-locale remount.
  useEffect(() => {
    if (locale) sessionStorage.setItem(SS_LOCALE, locale)
  }, [locale])

  // When the viewer's own queue resolves empty but the PR is still blocked
  // elsewhere, surface the team status by opening the "Your scope" panel — the
  // empty-state already shows the board inline; this also reveals the header
  // (copy-link etc.) above the worklist. One-shot: only while not already open.
  const autoRevealedRef = useRef(false)
  useEffect(() => {
    if (autoRevealedRef.current || scopeOpen) return
    if (loaded && !fetching && everFetched && queue.length === 0 && !current) {
      if (overview && overview.backlog.total > 0) {
        autoRevealedRef.current = true
        onScopeAutoOpen?.()
      }
    }
  }, [
    loaded,
    fetching,
    everFetched,
    queue.length,
    current,
    overview,
    scopeOpen,
    onScopeAutoOpen,
  ])

  // First-load spinner: scope still resolving, queue mid-fetch, or a locale is
  // chosen but its first fetch hasn't returned (covers the gap between scope
  // resolving and the queue effect firing, so the empty state never flashes).
  const loading =
    !loadError &&
    (!loaded || fetching || (locale !== null && !everFetched)) &&
    queue.length === 0

  // 3. scroll/highlight after a soft-nav lands on the target string
  useEffect(() => {
    if (!current) return
    const t = setTimeout(
      () => findAndHighlight(current.targetValue || current.enValue),
      250
    )
    return () => clearTimeout(t)
  }, [reviewParam, current])

  // Always jump to the best page for the current item in the reviewer's locale.
  // reviewHref falls back to the locale home when the exact page is unknown
  // (chrome/legal), so the reviewer always lands in the right country+locale
  // rather than getting stuck — the panel's banner explains any caveat.
  function navToCurrent() {
    const cur = getReviewState().current
    if (!cur) return
    const href = reviewHref(cur, indexLoc(cur))
    // `scroll: false` so Next doesn't reset scroll to the top of the page on
    // every advance — that top-reset is what read as the page "resetting", and
    // it forced findAndHighlight to smooth-scroll all the way back down "from
    // the top". With it off, the only motion is the highlight scrolling from
    // your current position to the next string.
    //
    // startTransition keeps the current page rendered while the next route
    // streams in, so a cross-locale advance (which remounts the route subtree)
    // no longer flashes a blank loading state and read as a "hard redirect" —
    // only the page content swaps, the cockpit rail stays put. (AHD-7494)
    if (href) startTransition(() => router.push(href, { scroll: false }))
  }
  // Reviewer-chosen jump from the "where this appears" panel: soft-nav to that
  // page, then highlight the string. The reviewParam highlight effect won't fire
  // here (same key → same ?review value across pages), so schedule the highlight
  // explicitly once the new page has streamed in.
  function jumpTo(href: string) {
    startTransition(() => router.push(href, { scroll: false }))
    const cur = getReviewState().current
    if (cur)
      setTimeout(() => findAndHighlight(cur.targetValue || cur.enValue), 350)
  }
  function open(i: number) {
    reviewActions.goto(i)
    navToCurrent()
  }
  function next() {
    reviewActions.next()
    navToCurrent()
  }
  function prev() {
    reviewActions.prev()
    navToCurrent()
  }
  function skip() {
    reviewActions.completeCurrent()
    navToCurrent()
  }
  function handleUndo() {
    if (undoLast()) navToCurrent()
  }
  // Jump from the batch table back into the focus flow on a specific string.
  function openInFocus(key: string) {
    const i = getReviewState().queue.findIndex((q) => q.key === key)
    if (i < 0) return
    reviewActions.setView("focus")
    open(i)
  }

  // Table view widens the docked cockpit rail to a full table; always undo it.
  useEffect(() => {
    document.documentElement.classList.toggle(
      "ahead-cockpit-wide",
      view === "table"
    )
    return () => document.documentElement.classList.remove("ahead-cockpit-wide")
  }, [view])

  // Commit pending sign-offs on a real page unload only. We deliberately do NOT
  // flush on component unmount: a cross-locale auto-advance remounts this body,
  // and flushing there would commit the just-approved item early — collapsing
  // its undo window and making the toast vanish mid-advance. The pending stack
  // is a module singleton with its own timers, so it survives the remount and
  // still commits on schedule. (AHD-7494)
  useEffect(() => {
    const onUnload = () => flushAll()
    window.addEventListener("beforeunload", onUnload)
    return () => window.removeEventListener("beforeunload", onUnload)
  }, [])

  // Keyboard: drive the current item without the mouse. ⌘/Ctrl+Enter works inside the
  // textarea; single-key actions are suppressed while typing in an editable field.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Table view has its own interactions; don't drive the focus panel/queue.
      if (getReviewState().view === "table") return
      if (!getReviewState().current) return
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault()
        panelRef.current?.approve()
        return
      }
      if (isEditableTarget(e.target)) return
      if (isUndoChord(e)) {
        e.preventDefault()
        handleUndo()
        return
      }
      switch (e.key) {
        case "a":
          e.preventDefault()
          panelRef.current?.approve()
          break
        case "e":
          e.preventDefault()
          panelRef.current?.focusEdit()
          break
        case "s":
          e.preventDefault()
          skip()
          break
        case "j":
          e.preventDefault()
          next()
          break
        case "k":
          e.preventDefault()
          prev()
          break
      }
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
    // skip/next/prev are stable closures over imported singletons + router
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (isMobile) {
    return (
      <MobileReviewView
        idToken={idToken}
        glossary={glossary}
        queue={queue}
        current={current}
        cursor={getReviewState().cursor}
        locate={currentLocate}
        places={currentPlaces}
        onPreview={jumpTo}
        filtered={filtered}
        availableTiers={availableTiers}
        tierSel={effTierSel}
        onToggleTier={toggleTier}
        hiddenByTier={hiddenByTier}
        onShowAllTiers={() => setTierSel(new Set())}
        locales={locales}
        locale={locale}
        onSetLocale={setLocale}
        onSkip={skip}
        onUndo={handleUndo}
        onOpen={open}
        loading={loading}
        overview={overview}
      />
    )
  }

  return (
    <div className="ahead-review-body">
      <UndoToast onUndo={handleUndo} />
      {scopeOpen && overview ? (
        <ReviewOverviewHeader overview={overview} />
      ) : null}
      {locales.length > 1 ? (
        <select
          value={locale ?? ""}
          onChange={(e) => setLocale(e.target.value)}
          aria-label="Review locale"
        >
          {locales.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
      ) : null}
      <div
        className="ahead-review-viewtoggle"
        role="tablist"
        aria-label="Review view"
      >
        <button
          role="tab"
          aria-selected={view === "focus"}
          className={view === "focus" ? "on" : ""}
          onClick={() => reviewActions.setView("focus")}
        >
          Focus
        </button>
        <button
          role="tab"
          aria-selected={view === "table"}
          className={view === "table" ? "on" : ""}
          onClick={() => reviewActions.setView("table")}
        >
          Table
        </button>
      </div>
      {view === "table" && locale ? (
        <BatchTable
          idToken={idToken}
          locale={locale}
          onOpenFocus={openInFocus}
        />
      ) : (
        <>
          {queue.length > 0 ? (
            <>
              {availableTiers.length > 1 ? (
                <div
                  className="ahead-review-tiers"
                  role="group"
                  aria-label="Filter by tier"
                >
                  {availableTiers.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className="ahead-review-tier"
                      data-tier={t}
                      aria-pressed={effTierSel.has(t)}
                      onClick={() => toggleTier(t)}
                    >
                      {TIER_LABEL[t]}
                    </button>
                  ))}
                </div>
              ) : null}
              <input
                className="ahead-review-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search strings…"
                aria-label="Search strings to review"
              />
              <div className="ahead-review-head">
                {filtered.length !== queue.length
                  ? `${filtered.length} of ${queue.length} shown`
                  : `${queue.length} to review (${
                      queue.filter((i) => i.status === "stale").length
                    } stale)`}
              </div>
              {hiddenByTier > 0 ? (
                <div className="ahead-review-tierhint" role="note">
                  {hiddenByTier} more{" "}
                  {hiddenByTier === 1 ? "string is" : "strings are"} hidden by
                  the tier filter.{" "}
                  <button
                    type="button"
                    className="ahead-review-tierhint-clear"
                    onClick={() => setTierSel(new Set())}
                  >
                    Show all tiers
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
          {loading ? (
            <div
              className="ahead-review-loading"
              role="status"
              aria-live="polite"
            >
              <span className="ahead-review-spinner" aria-hidden="true" />
              Loading strings…
            </div>
          ) : null}
          {/* Current item's editor stays pinned at the top — acting on it must
          never require scrolling past the whole queue. */}
          {current ? (
            <ReviewPanel
              ref={panelRef}
              key={`${current.key}:${current.locale}`}
              item={current}
              idToken={idToken}
              glossary={glossary}
              locate={currentLocate}
              places={currentPlaces}
              onPreview={jumpTo}
              pageHref={pageHref}
              onDone={skip}
            />
          ) : null}
          {loaded && loadError ? (
            <div className="ahead-review-empty" role="status">
              <p>
                ⚠ Couldn&apos;t load review data — the review database is
                unreachable.
              </p>
            </div>
          ) : null}
          {loaded &&
          !loadError &&
          !loading &&
          !fetching &&
          queue.length === 0 &&
          !current ? (
            <div className="ahead-review-empty" role="status">
              {locales.length === 0 ? (
                overview && overview.blocked.length ? (
                  <p>
                    The locale{overview.blocked.length === 1 ? "" : "s"}{" "}
                    you&apos;re assigned aren&apos;t open to you yet — see the
                    note above to unlock{" "}
                    {overview.blocked.length === 1 ? "it" : "them"}.
                  </p>
                ) : (
                  <p>
                    You have no review scope assigned yet. Ask an admin to add
                    you to the <code>translation_reviewer</code> table.
                  </p>
                )
              ) : overview && overview.backlog.total > 0 ? (
                <>
                  <p>
                    ✅ Your locales are signed off. This PR is still blocked on{" "}
                    {overview.localeStatus
                      .filter((r) => r.count > 0)
                      .map((r) => r.locale)
                      .join(", ")}
                    .
                  </p>
                  {!scopeOpen ? (
                    <PrStatusBoard
                      rows={overview.localeStatus}
                      reviewLink={
                        typeof window === "undefined"
                          ? ""
                          : window.location.href
                      }
                    />
                  ) : null}
                </>
              ) : overview ? (
                <p>🎉 All locales signed off — this PR is green.</p>
              ) : null}
            </div>
          ) : null}
          <ul>
            {filtered.slice(0, visible).map(({ item, i }) => {
              const loc = localeOf(item)
              return (
                <li key={`${item.key}:${item.locale}`}>
                  <button
                    onClick={() => open(i)}
                    aria-current={item === current}
                  >
                    {item.key}{" "}
                    {TIER_MARKER[item.tier] ? (
                      <span
                        className="ahead-review-tier-marker"
                        data-tier={item.tier}
                        role="img"
                        aria-label={TIER_ARIA[item.tier]}
                      >
                        {TIER_MARKER[item.tier]}
                      </span>
                    ) : null}
                    {LOC_MARKER[loc] ? (
                      <span
                        className="ahead-review-loc-marker"
                        title={
                          loc === "chrome"
                            ? "Shared UI — not on one page"
                            : "Has placeholders — won't highlight"
                        }
                      >
                        {" "}
                        {LOC_MARKER[loc]}
                      </span>
                    ) : null}{" "}
                    <small>{item.status}</small>
                  </button>
                </li>
              )
            })}
          </ul>
          {filtered.length > visible ? (
            <button
              className="ahead-review-showmore"
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
            >
              Show {Math.min(PAGE_SIZE, filtered.length - visible)} more (
              {filtered.length - visible} hidden)
            </button>
          ) : null}
          {current ? (
            <div className="ahead-review-legend">
              A approve · E edit · S skip · J/K next/prev · ⌘↵ approve
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
